import styled from "styled-components";
import { COLORS, DARK_THEME_COLORS } from "styles/colors";
import { FLEX } from "styles/flex";
import { calcRem, getSummaryCardStyle } from "styles/theme";
import * as mediaQuery from "components/MediaQuery";
import InnerContainer, {
  InnerPaddingWrapper,
  InnerPX,
} from "components/atoms/inner/InnerContainer";
import { TransitionType } from "api/interfaces/widgetInterface.interface";
import {
  collapsedSidebarWidth,
  discollapsedSidebarWidth,
} from "components/blocks/sidenav/SideNavBarStyled";

export const StatsFixBar = styled(InnerPaddingWrapper)<{
  noticeHeight?: number;
  collapsed?: boolean;
}>`
  position: fixed;
  top: calc(${({ noticeHeight }) => noticeHeight}px + 50px);
  left: calc(
    ${({ collapsed }) =>
      collapsed ? discollapsedSidebarWidth : collapsedSidebarWidth}px
  );
  right: 0;
  height: 40px;
  font-size: ${calcRem(14)};
  color: ${({ theme }) => theme.COLORS.TEXT};
  background-color: ${({ theme }) => theme.COLORS.BACKGROUND};
  border-bottom: 1px solid
    ${({ theme }) =>
      theme.value === "light"
        ? theme.COLORS.LIGHTGRAYBORDER
        : theme.COLORS.PRIMARY3};
  z-index: 899;
  ${FLEX.FlexEndCenter};
  width: auto;
  gap: 10px;
  ${mediaQuery.isMobile} {
    left: 0;
  }
  .ant-switch[aria-checked="false"] {
    background-color: ${({ theme }) =>
      theme.value === "light"
        ? theme.COLORS.BORDER
        : theme.COLORS.LIGHTGRAYBORDER};
  }
`;
export const Inner = styled(InnerContainer)<{ first?: boolean }>`
  height: 100%;
  ${FLEX.FlexStartBetween};
  gap: 20px;
  flex-direction: column;

  ${mediaQuery.isDefault} {
    box-sizing: border-box;
    padding: 20px ${InnerPX}px;
  }
  ${mediaQuery.isMobile} {
    padding: 30px;
    overflow: hidden;
  }
`;

export const Container = styled(InnerPaddingWrapper)<{
  noticeHeight?: number;
  isDraggable?: boolean;
}>`
  width: 100%;
  min-height: calc(100dvh - ${({ noticeHeight }) => noticeHeight}px - 50px);
  padding-top: 60px;
  padding-bottom: 20px;
  overflow: hidden;
  ${mediaQuery.isMobile} {
    padding: 105px 15px 15px;
    .react-grid-layout {
      ${FLEX.FlexStartCenter};
      flex-direction: column;
      gap: 15px;
      height: auto !important;
    }
    .react-grid-item {
      height: auto !important;
      position: relative !important;
      transform: none !important;
      width: 100% !important;
    }
  }

  .react-grid-layout + .react-grid-layout {
    margin-top: 20px;
  }
  .ant-switch {
    width: 24px;
    height: 12px;
    min-width: 24px;
    ${({ theme }) =>
      theme.value === "dark" &&
      `
      border: 1px solid ${theme.COLORS.BORDER};
    `}
    &.ant-switch-checked .ant-switch-handle {
      inset-inline-start: 0;
      right: 1px;
      left: auto;
    }
    .ant-switch-handle {
      width: 10px;
      height: 10px;
      top: ${({ theme }) => (theme.value === "dark" ? "0" : "1px")};
      inset-inline-start: 1px;
    }
  }
  .ant-switch-inner {
    display: none;
  }
  ${({ isDraggable, theme }) =>
    isDraggable &&
    `
    .chart-layout .react-grid-item {
      cursor: move;
      &:after {
        content: "Drag to move";
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background-color: ${COLORS.BLACK}70;
        ${FLEX.FlexCenterCenter};
        color: ${COLORS.WHITE};
        border-radius: 5px;
      }
      .react-resizable-handle-se {
        z-index: 1;
      }
      > div {
        filter: blur(1px);
      }
    }
  `}
`;

export const UserIcon = styled.div`
  ${FLEX.FlexStartCenter};
  svg {
    margin: 0 -10px;
  }
`;

const StatsHeader = styled.h5`
  font-size: ${calcRem(12.8)};
  letter-spacing: ${calcRem(1)};
  font-weight: 500;
  color: ${({ theme }) =>
    theme.value === "light"
      ? theme.COLORS.BUTTONS3
      : DARK_THEME_COLORS.LIGHTGRAY};
  text-transform: uppercase;
`;

export const Summary = {
  Container: styled.div`
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 15px;
    padding-bottom: 15px;
    ${mediaQuery.isTablet} {
      grid-template-columns: repeat(2, 1fr);
    }
    ${mediaQuery.isMobile} {
      grid-template-columns: repeat(1, 1fr);
    }
  `,
  CameraList: styled.div`
    display: grid;
    grid-template-columns: auto min-content min-content;
    gap: 6px;
    column-gap: 15px;
    width: 100%;
    font-size: ${calcRem(12.8)};
    label {
      color: ${({ theme }) => theme.COLORS.WHITE}80;
    }
    span {
      color: ${({ theme }) => theme.COLORS.WHITE};
      ${FLEX.FlexBetweenCenter};
      gap: 5px;
      &.text-right {
        justify-content: flex-end;
      }
    }
  `,
  Icon: styled.div`
    position: absolute;
    top: 0;
    right: 0;
    font-size: ${calcRem(26)};
    color: ${({ theme }) =>
      theme.value === "light"
        ? theme.COLORS.BUTTONS3
        : DARK_THEME_COLORS.LIGHTGRAY};
  `,
  List: styled.ul`
    ${FLEX.FlexEndStart};
    flex-direction: column;
    width: 100%;
    gap: 6px;
    width: 100%;
    li {
      ${FLEX.FlexBetweenCenter};
      width: 100%;
      font-size: ${calcRem(12.8)};
      color: ${({ theme }) => theme.COLORS.WHITE}80;
      span {
        color: ${({ theme }) => theme.COLORS.WHITE};
      }
    }
  `,
  Total: styled.div<{ status: number }>`
    font-size: ${calcRem(32)};
    font-weight: 600;
    padding-top: 3px;
    color: ${({ theme }) => theme.COLORS.WHITE};
    gap: 10px;
    ${FLEX.FlexStartCenter};
    letter-spacing: -0.02em;
  `,
  Transition: styled.div<{ transition: number }>`
    ${FLEX.FlexStartEnd};
    gap: 4px;
    color: ${({ theme, transition }) =>
      Number(transition) > 0
        ? COLORS.BUTTONS2
        : Number(transition) < 0
        ? theme.COLORS.RED
        : theme.COLORS.WHITE};
    font-size: ${calcRem(13)};
    font-weight: 500;
  `,
  CardHeader: styled.div`
    position: relative;
    ${FLEX.FlexStartBetween};
    flex-direction: column;
    gap: 7px;
    width: 100%;
  `,
  CardTitle: styled(StatsHeader)``,
  Card: styled.div`
    ${FLEX.FlexStartStart};
    flex-direction: column;
    gap: 15px;
    ${({ theme }) => getSummaryCardStyle(theme)}
    padding: 15px;
    width: 100%;
    min-height: 165px;
  `,
};

export const Chart = {
  RemoveButton: styled.button`
    ${FLEX.FlexCenterCenter};
    padding: 5px;
    position: absolute;
    top: 16px;
    right: 28px;
    path {
      stroke: ${({ theme }) => theme.COLORS.WHITE}70;
    }
    &:hover {
      path {
        stroke: ${({ theme }) => theme.COLORS.WHITE};
      }
    }
  `,
  MoreButton: styled.button`
    ${FLEX.FlexCenterCenter};
    padding: 5px;
    color: ${({ theme }) =>
      theme.value === "light"
        ? theme.COLORS.BUTTONS3
        : DARK_THEME_COLORS.LIGHTGRAY};
  `,
  WidgetSettingsContent: styled.ul`
    ${FLEX.FlexStartBetween};
    flex-direction: column;
    width: 100%;
    background-color: ${({ theme }) => theme.COLORS.WIDGETCOLOR};
    padding: 5px 0;
    border-radius: 5px;
    border: 1px solid ${({ theme }) => theme.COLORS.BORDER};
    .remove {
      button {
        color: ${({ theme }) => theme.COLORS.VALIDATERED};
      }
    }
    button {
      ${FLEX.FlexStartCenter};
      padding: 7px 10px;
      font-size: ${calcRem(14)};
      width: 100%;
      color: ${({ theme }) => theme.COLORS.WHITE};
      &:hover {
        background-color: ${({ theme }) => theme.COLORS.WHITE}05;
      }
    }
  `,
  SwitchContainer: styled.div<{ $color?: string }>`
    .ant-switch.ant-switch-checked,
    .ant-switch.ant-switch-checked:hover {
      background-color: ${({ $color }) => $color};
    }
  `,
  DonutChartWrapper: styled.div`
    width: 140px;
    height: 140px;
    position: relative;
    ${mediaQuery.isMobile} {
      margin-top: 10px;
    }
  `,
  ChildItem: styled.div`
    ${FLEX.FlexBetweenCenter};
    gap: 5px;
    padding: 2px 0;
    color: ${({ theme }) => theme.COLORS.WHITE}80;
  `,
  ChildItemLabel: styled.div`
    font-size: ${calcRem(12)};
    font-weight: 400;
    white-space: nowrap;
  `,
  ChildItemValue: styled.div`
    font-size: ${calcRem(12)};
    font-weight: 400;
    color: ${({ theme }) => theme.COLORS.WHITE};
  `,
  DonutChartCenter: styled.div`
    ${FLEX.FlexCenterCenter};
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    flex-direction: column;
    gap: 12px;
  `,
  DonutChartCenterPercent: styled.div`
    font-size: ${calcRem(24)};
    font-weight: 500;
    color: ${({ theme }) => theme.COLORS.WHITE};
  `,
  DonutChartCenterLabel: styled.div`
    font-size: ${calcRem(14)};
    color: ${({ theme }) =>
      theme.value === "light"
        ? theme.COLORS.BUTTONS3
        : `${theme.COLORS.WHITE}80`};
  `,
  LegendList: styled.ul`
    ${FLEX.FlexStartBetween};
    flex-direction: column;
    gap: 10px;
    width: 100%;
    padding: 0 15px 15px;
  `,
  LegendListItem: styled.li`
    ${FLEX.FlexBetweenCenter};
  `,
  Card: styled(Summary.Card)`
    padding: 0;
    gap: 0;
    height: 100%;
    .spinner-wrapper {
      padding-top: 0;
      flex: 1;
    }
  `,
  GraphContainer: styled.div`
    flex: 1;
    ${FLEX.FlexStartCenter};
    gap: 15px;
    padding-top: 15px;
    flex-direction: column;
    width: 100%;
  `,
  Header: styled.div<{ isMobile?: boolean; columnCount?: number }>`
    width: 100%;
    padding: 15px 35px 0 15px;
    ${FLEX.FlexBetweenCenter};
    gap: 5px;
    flex-wrap: wrap;
  `,
  GraphWrapper: styled.div<{
    isShowButton?: boolean;
    isShowLegend?: boolean;
    reduced?: boolean;
  }>`
    width: 100%;
    padding: 0 15px;
    height: ${({ isShowButton, isShowLegend, reduced }) =>
      (isShowButton ? (isShowLegend ? 210 : 250) : 300) - (reduced ? 35 : 0)}px;
    svg {
      overflow: visible;
    }
  `,
  HeaderInterval: styled.div<{ useintervalOneOptions?: boolean }>`
    ${FLEX.FlexEndCenter};
    padding-right: 20px;
    .ant-select {
      min-width: 90px;
    }
    .ant-select-selector {
      height: 26px !important;
      min-height: 26px !important;
      .ant-select-selection-item {
        > div {
          height: auto;
        }
        line-height: 1;
      }
    }
    ${mediaQuery.isMobile} {
      .ant-select {
        flex: 1;
      }
    }
  `,
  HeaderTitle: styled(StatsHeader)`
    ${FLEX.FlexStartCenter};
    min-height: 26px;
  `,
  LabelButton: styled.button<{ active: boolean }>`
    ${FLEX.FlexCenterCenter};
    border-radius: 999px;
    padding: 6px 14px;
    border: 1px solid
      ${({ theme, active }) =>
        active
          ? "transparent"
          : theme.value === "light"
          ? theme.COLORS.BORDER
          : theme.COLORS.BACKGROUND};
    background: ${({ active, theme }) =>
      active
        ? theme.COLORS.WHITE
        : theme.value === "light"
        ? "transparent"
        : theme.COLORS.BACKGROUND};
    color: ${({ active, theme }) =>
      active
        ? theme.value === "light"
          ? COLORS.WHITE
          : theme.COLORS.BLACK
        : theme.value === "light"
        ? COLORS.BORDER
        : theme.COLORS.LIGHTGRAY};
    font-size: ${calcRem(12)};
    font-weight: 500;
    cursor: pointer;
  `,
  GraphTypeButton: styled.button<{ $active?: boolean; right?: boolean }>`
    background-color: ${({ theme, $active }) =>
      $active ? `${theme.COLORS.WHITE}` : theme.COLORS.BACKGROUND};
    border: 1px solid
      ${({ theme }) =>
        theme.value === "light"
          ? theme.COLORS.BORDER
          : theme.COLORS.BACKGROUND};
    color: ${({ theme, $active }) =>
      $active
        ? theme.value === "light"
          ? COLORS.WHITE
          : COLORS.BLACK
        : `${theme.COLORS.WHITE}70`};
    font-size: ${calcRem(12)};
    gap: 4px;
    padding: 5px 10px;
    ${FLEX.FlexCenterCenter};
    cursor: pointer;
    margin-left: 10px;
    border-radius: ${({ right }) => (right ? "0 3px 3px 0" : "3px 0 0 3px")};
    ${({ right }) =>
      right &&
      `
      border-left: none;
      margin-left: 0;
    `}
  `,
  HeaderButtons: styled.div`
    gap: 10px;
    padding-top: 20px;
    display: block;
    overflow: auto;
    width: 100%;
    white-space: nowrap;
    ${mediaQuery.isMobile} {
      ${FLEX.FlexStartStart};
      flex-direction: column;
      gap: 5px;
    }
    padding: 5px 20px 10px;
    button {
      display: inline-block;
      & + button {
        margin-left: 5px;
      }
    }
  `,
  Legend: styled.div`
    ${FLEX.FlexAroundStart};
    width: 100%;
    padding: 18px;
    gap: 12px;
    border-top: 1px solid
      ${({ theme }) =>
        theme.value === "light"
          ? theme.COLORS.LIGHTGRAYBORDER
          : theme.COLORS.BACKGROUND};
  `,
  LegendItem: styled.div`
    ${FLEX.FlexCenterCenter};
    &.chart-tooltip-item {
      ${FLEX.FlexCenterStart};
    }
    flex-direction: column;
    gap: 5px;
  `,
  LegendItemLabel: styled.div<{ $color?: string; isListType?: boolean }>`
    font-size: ${({ isListType }) =>
      isListType ? calcRem(13.5) : calcRem(13)};
    font-weight: 400;
    color: ${({ theme }) => theme.COLORS.WHITE}80;
    ${({ isListType }) => (isListType ? `&:before` : `&:after`)} {
      content: "";
      width: ${({ isListType }) => (isListType ? 7 : 6)}px;
      height: ${({ isListType }) => (isListType ? 7 : 6)}px;
      border-radius: 999px;
      background: ${({ $color }) => $color};
      display: inline-block;
      vertical-align: ${({ isListType }) => (isListType ? "middle" : "top")};
      margin-${({ isListType }) => (isListType ? "right" : "left")}: 4px;
      margin-top: ${({ isListType }) => (isListType ? "-2px" : "0")};
    }
  `,
  LegendItemValue: styled.div<{
    fontSize?: number;
    isListType?: boolean;
    $color?: string;
  }>`
    font-size: ${({ fontSize, isListType }) =>
      fontSize ? calcRem(fontSize) : calcRem(isListType ? 13.5 : 24)};
    font-weight: ${({ isListType }) => (isListType ? 400 : 600)};
    color: ${({ theme, $color }) => ($color ? $color : theme.COLORS.WHITE)};
    ${({ isListType }) => isListType && `text-align: right;`}
  `,
  Tooltip: styled.div`
    padding: 8px 10px;
    background: ${({ theme }) =>
      theme.value === "light"
        ? theme.COLORS.SIDEBARBG
        : theme.COLORS.BACKGROUND};
    border: 1px solid ${({ theme }) => theme.COLORS.BORDER};
    border-radius: 5px;
    font-size: ${calcRem(12)};
  `,
};

export const RangeLegend = styled(Chart.Legend)`
  justify-content: flex-end;
  border-top: none;
  padding-bottom: 25px;
`;

export const DragHandle = styled.span`
  position: absolute;
  top: 16px;
  right: 10px;
  cursor: move;
  color: ${({ theme }) => theme.COLORS.WHITE};
  font-size: ${calcRem(12)};
  padding: 5px;
  ${FLEX.FlexCenterCenter};
  border-radius: 3px;
`;

export const ResizeHandle = styled.span`
  cursor: nwse-resize;
  position: absolute;
  bottom: 0;
  right: 0;
`;
